'use client'

import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { getOwner, useRightPanelContext } from './context.tsx'
import type { RightPanelId } from './context.tsx'

type RightPanelProps = {
  children: ReactNode
  contentKey?: number | string
  defaultOpen?: boolean
  id: RightPanelId
  ownerLifecycle?: ReactNode
  title: ReactNode
  width?: string
}

/** Registers a named panel and portals its caller-owned children into the shell. */
const RightPanel = ({
  children,
  contentKey,
  defaultOpen = false,
  id,
  ownerLifecycle,
  title,
  width,
}: RightPanelProps) => {
  const {
    activePanelId,
    hasBeenOpened,
    open,
    openDefaultPanel,
    openMobile,
    portalHost,
    registerPanel,
    registrations,
    updatePanel,
  } = useRightPanelContext()
  const defaultOpenConsumed = useRef(false)
  const [instanceId] = useState(() => Symbol(id))
  const isOwner = getOwner(registrations, id)?.instanceId === instanceId

  useEffect(
    () => registerPanel({ contentKey, id, instanceId, title, width }),
    [id, instanceId, registerPanel],
  )

  useEffect(() => {
    updatePanel({ contentKey, id, instanceId, title, width })
  }, [contentKey, id, instanceId, title, updatePanel, width])

  useEffect(() => {
    if (defaultOpen && !defaultOpenConsumed.current && openDefaultPanel(id)) {
      defaultOpenConsumed.current = true
    }
  }, [defaultOpen, id, open, openDefaultPanel, openMobile])

  const lifecycle = isOwner ? ownerLifecycle : null

  if (!portalHost || activePanelId !== id || !hasBeenOpened || !isOwner) {
    return lifecycle
  }

  return (
    <>
      {lifecycle}
      {createPortal(children, portalHost)}
    </>
  )
}

export { RightPanel }
